<template>
  <div v-if="currentPrice" class="gap-050">
    <section
      ref="scrollContent"
      :class="{
        ['scrollable fade-out']: isScrollable,
        'at-bottom': isAtBottom,
      }"
    >
      <div class="flex flex-col gap-200" :class="{ 'scrollable-content': isScrollable }">
        <LcDescriptionList
          v-for="(group, index) in currentPrice.groupedPlays"
          :key="index"
          class="gap-none"
        >
          <PriceListPerPlay
            v-if="isNumberOrDateLottery"
            class="group"
            v-bind="{
              name: group.name,
              numberOfPlays: group.plays,
              lines: group.virtualPlays,
            }"
          >
            {{ group.name !== 'extrahit' ? $t(`lottery.name.${lotteryId}`) : '' }}
            {{
              group.name === 'extrahit'
                ? $t(`lottery.${lotteryId}.${group.name}`)
                : $t('common.words.line')
            }}
          </PriceListPerPlay>
          <PriceListPerPlay
            v-if="!isNumberOrDateLottery"
            class="group"
            v-bind="{ numberOfPlays: group.plays }"
          >
            {{ formatMoney(group.wagerAmount as Money, { fractionDigits: 0 }) }}
            {{ getPerPlayTranslation(lotteryId, 'betType', String(group.betType)) }}
          </PriceListPerPlay>
          <dd>{{ formatMoney(group.price.sum) }}</dd>
          <template v-for="addOn of group.addOns" :key="addOn.name">
            <dt>
              {{ $t('tiertable.fielddivider') }}
              {{ $t(`lottery.addon.${addOn.name}`) }}
            </dt>
            <dd>{{ formatMoney(addOn.price.sum) }}</dd>
          </template>
        </LcDescriptionList>
      </div>
    </section>

    <LcDescriptionList
      v-if="prepaidSubscriptionAmount"
      class="prepaid-subscription py-025 px-050 rounded-s mt-150"
    >
      <dt>
        {{ $t('lottery.pricebreakdown.prepaiddraws') }}
      </dt>
      <dd class="flex justify-end font-medium">
        {{
          $t('lottery.pricebreakdown.prepaiddraws.amount', {
            amount: prepaidSubscriptionAmount,
          })
        }}
      </dd>
    </LcDescriptionList>
  </div>
</template>

<script lang="ts" setup>
import type { GroupedPlays } from '@lottocom/price-calculation'

import Money from '~~/models/Money'

import type {
  ChargeForPlay,
  Money as MoneyType,
  PriceBreakdown,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

type LotteryPriceBreakdownListProps = {
  currentPrice?: Maybe<{
    chargesPerPlay: ChargeForPlay[]
    feePerPlaySlip: MoneyType
    groupedPlays: GroupedPlays[] | PriceBreakdown['groupedPlays']
    numberOfPlays: number
    playChargePerPlaySlip: MoneyType
  }>
  prepaidSubscriptionAmountCheckout?: number
  scrollable?: boolean
}

const props = defineProps<LotteryPriceBreakdownListProps>()

const { lotteryId, prepaidSubscriptionAmount: prepaidSubscriptionAmountPlaySlip } = usePlaySlip()

const { isDateLottery, isNumberLottery } = useLotteryType(lotteryId)

const { path } = useRoute()

const { getPerPlayTranslation } = useTranslations()

const scrollContent = ref<Maybe<HTMLElement>>(null)
const isAtBottom = ref(false)

const checkOverflow = () => {
  isAtBottom.value = false
  handleScroll()
}

const handleScroll = () => {
  if (!scrollContent.value) return
  const { clientHeight, scrollHeight, scrollTop } = scrollContent.value
  isAtBottom.value = scrollTop + clientHeight >= scrollHeight - 1
}

const hasOverflow = computed(() => {
  if (!scrollContent.value) return false
  const { scrollHeight } = scrollContent.value
  return scrollHeight > 256
})

const isNumberOrDateLottery = computed(() => isNumberLottery.value || isDateLottery.value)

const isScrollable = computed(() => props.scrollable && hasOverflow.value)

const prepaidSubscriptionAmount = computed(() =>
  path.includes('checkout')
    ? props.prepaidSubscriptionAmountCheckout
    : prepaidSubscriptionAmountPlaySlip.value?.value,
)
const prices = computed(() => ({
  playChargePerPlay: new Money(props.currentPrice?.chargesPerPlay[0]?.baseCharge),
  playChargePerPlaySlip: new Money(props.currentPrice?.playChargePerPlaySlip),
}))

onMounted(() => {
  nextTick(() => {
    if (props.scrollable && scrollContent.value) {
      scrollContent.value.addEventListener('scroll', handleScroll)
      checkOverflow()
    }
  })
})

onBeforeUnmount(() => {
  if (props.scrollable && scrollContent.value) {
    scrollContent.value.removeEventListener('scroll', handleScroll)
  }
})
</script>

<style lang="scss" scoped>
.prepaid-subscription {
  background-color: token('color/background/accent/success/success/light');
}

.scrollable {
  overflow-y: auto;
  max-height: 256px;

  &::-webkit-scrollbar {
    width: 0.5rem;
  }

  &::-webkit-scrollbar-track {
    background-color: token('color/background/secondary/secondary');
  }

  &::-webkit-scrollbar-thumb {
    border-radius: token('size/radius/s');
    background-color: token('color/background/accent/accent');
  }

  @supports (-moz-appearance: none) {
    scrollbar-color: token('color/background/accent/accent') transparent;
    scrollbar-width: auto;
  }

  &.fade-out {
    mask-image: linear-gradient(to bottom, black 80%, transparent 100%);
  }

  &.at-bottom {
    mask-image: none;
  }

  .scrollable-content {
    height: 100%;
  }
}
</style>
